// React
import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useState } from "react";

// @mui material components
import { Card, CircularProgress, Fade, Grid, Icon, InputLabel, Modal, Tooltip } from "@mui/material";

import { gql, useApolloClient, useMutation } from "@apollo/client";
import ModalConfirm from "components/Modals/Confirm";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { formatDateARG, formatMoneyPunto } from "utils/formatters";
import { downloadPagoCuentaCorrienteExcel } from "../utils/pagoCuentaCorrienteExcel";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "92%",
  width: { xs: "92%", sm: "80%", xxl: "68%" },
  overflowY: "auto",
  borderRadius: 2,
  boxShadow: "0 24px 48px rgba(15, 23, 42, 0.2)",
};

const PAGAR_CUENTA_CORRIENTE = gql`
  mutation pagosCuentaCorriente(
    $fechaFin: Date
    $informacionGeneral: String
    $leyenda: String
    $idSede: Int
    $pagos: [PagoCuentaCorrienteParcialInput!]
    $tipoCliente: TipoClienteCuentaCorriente
  ) {
    pagosCuentaCorriente(
      fechaFin: $fechaFin
      informacionGeneral: $informacionGeneral
      leyenda: $leyenda
      idSede: $idSede
      pagos: $pagos
      tipoCliente: $tipoCliente
    ) {
      message
      idLote
    }
  }
`;

const GET_PAGO_CC_HISTORIAL = gql`
  query pagoCuentaCorrienteHistorial($idLote: Int!) {
    pagoCuentaCorrienteHistorial(idLote: $idLote) {
      id
      fechaInicio
      fechaFin
      createdAt
      informacionGeneral
      estado
      comprobanteFile
      cantidadClientes
      montoTotal
      detalles {
        idCliente
        nombre
        apellido
        dni
        saldoAntes
        montoPago
        saldoActual
      }
    }
  }
`;

const normalizeSearch = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

export default function ModalCargarPago({
  open,
  handleClose,
  fechaFin,
  idSede,
  getReporte,
  clientes = [],
  onPagoRegistrado,
}) {
  const { handleSnackbar } = useContext(MessageManager);
  const apolloClient = useApolloClient();
  const [datos, setDatos] = useState({
    informacionGeneral: "",
    leyenda: "",
  });
  const [searchCliente, setSearchCliente] = useState("");
  const [pagosCliente, setPagosCliente] = useState([]);

  useEffect(() => {
    if (!open) return;

    setDatos({
      informacionGeneral: "",
      leyenda: "",
    });
    setSearchCliente("");

    setPagosCliente(
      (clientes || []).map((item) => {
        const montoMaximoPagable = Number(item.montoMaximoPagable) || 0;
        return {
          idCliente: Number(item.id),
          nombre: `${item.nombre || ""} ${item.apellido || ""}`.trim(),
          dni: item.dni,
          saldoActual: Number(item.saldoActual) || 0,
          saldoFechaCorte: Number(item.saldoFechaCorte) || 0,
          montoMaximoPagable,
          monto: montoMaximoPagable,
        };
      })
    );
  }, [open, clientes]);

  const handleChange = (e) => {
    setDatos({
      ...datos,
      [e.target.name]: e.target.value,
    });
  };

  const handleChangeMonto = (idCliente, value) => {
    setPagosCliente((prev) =>
      prev.map((item) => (item.idCliente === idCliente ? { ...item, monto: value } : item))
    );
  };

  const completarMontoCliente = (idCliente) => {
    setPagosCliente((prev) =>
      prev.map((item) =>
        item.idCliente === idCliente
          ? {
            ...item,
            monto: item.montoMaximoPagable,
          }
          : item
      )
    );
  };

  const limpiarMontoCliente = (idCliente) => {
    setPagosCliente((prev) =>
      prev.map((item) =>
        item.idCliente === idCliente
          ? {
            ...item,
            monto: 0,
          }
          : item
      )
    );
  };

  const pagosClienteFiltrados = useMemo(() => {
    const termino = normalizeSearch(searchCliente);
    if (!termino) return pagosCliente;

    return pagosCliente.filter((item) => {
      const nombre = normalizeSearch(item.nombre);
      const dni = String(item.dni || "");
      return nombre.includes(termino) || dni.includes(termino);
    });
  }, [pagosCliente, searchCliente]);

  const pagosSeleccionados = useMemo(() => {
    return pagosCliente
      .map((item) => ({
        idCliente: item.idCliente,
        monto: parseFloat(item.monto),
      }))
      .filter((item) => Number.isFinite(item.monto) && item.monto > 0);
  }, [pagosCliente]);

  const hayMontosInvalidos = useMemo(() => {
    return pagosCliente.some((item) => {
      const monto = Number(item.monto);
      if (item.monto === null) return false;
      if (!Number.isFinite(monto) || monto < 0) return true;
      return monto - Number(item.montoMaximoPagable || 0) > 0.01;
    });
  }, [pagosCliente]);

  const totalSeleccionado = useMemo(() => {
    return pagosSeleccionados.reduce((acc, item) => acc + item.monto, 0);
  }, [pagosSeleccionados]);

  const [openConfirm, setOpenConfirm] = useState(false);
  const handleOpenConfirm = () => setOpenConfirm(true);
  const handleCloseConfirm = () => setOpenConfirm(false);

  const [pagarCuentaCorriente, { loading: loadingPagar }] = useMutation(PAGAR_CUENTA_CORRIENTE, {
    onCompleted: async (response) => {
      const loteId = Number(response?.pagosCuentaCorriente?.idLote) || null;
      let descargaOk = false;

      if (loteId) {
        try {
          const { data: dataHistorial } = await apolloClient.query({
            query: GET_PAGO_CC_HISTORIAL,
            variables: { idLote: loteId },
            fetchPolicy: "no-cache",
          });
          descargaOk = downloadPagoCuentaCorrienteExcel(dataHistorial?.pagoCuentaCorrienteHistorial);
        } catch (error) {
          handleSnackbar(
            "Traslado registrado. No se pudo descargar automaticamente el Excel, podes descargarlo desde el historial.",
            "warning"
          );
        }
      }

      try {
        await Promise.resolve(getReporte?.());
      } catch (error) {
        // Refetch visual no bloqueante.
      }

      onPagoRegistrado?.();
      handleCloseConfirm();
      handleClose();
      handleSnackbar(
        descargaOk ? "Traslado realizado con exito. Excel descargado." : "Traslado realizado con exito",
        "success"
      );
    },
    onError: (error) => {
      handleSnackbar(error.message, "error");
    },
  });

  const handlePagar = () => {
    pagarCuentaCorriente({
      variables: {
        fechaFin,
        informacionGeneral: datos.informacionGeneral,
        leyenda: datos.leyenda,
        idSede,
        tipoCliente: "NO_FUNCIONARIO",
        pagos: pagosSeleccionados.map((item) => ({
          idCliente: item.idCliente,
          monto: parseFloat(item.monto.toFixed(2)),
        })),
      },
    });
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={loadingPagar ? null : handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox
            px={3}
            py={2.5}
            sx={{
              borderBottom: "1px solid #E7EDF5",
              background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
            }}
          >
            <SoftBox display="flex" justifyContent="space-between" alignItems="center">
              <SoftBox>
                <SoftTypography variant="h5" fontWeight="bold">
                  Trasladar deuda de clientes en cuenta corriente a Upro
                </SoftTypography>
                <SoftTypography variant="button" color="text">
                  Carga montos por cliente y confirma el traslado.
                </SoftTypography>
              </SoftBox>
              <Tooltip title="Cerrar" placement="top">
                <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer", color: "#334E72" }}>
                  close
                </Icon>
              </Tooltip>
            </SoftBox>
          </SoftBox>

          <SoftBox p={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <InputLabel htmlFor="informacionGeneral">
                  <SoftTypography variant="caption" fontWeight="bold">
                    Información general
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  id="informacionGeneral"
                  name="informacionGeneral"
                  value={datos.informacionGeneral}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder="Agrega información adicional sobre el traslado"
                />
              </Grid>
              <Grid item xs={12}>
                <InputLabel htmlFor="leyenda">
                  <SoftTypography variant="caption" fontWeight="bold">
                    Leyenda para el cliente
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  id="leyenda"
                  name="leyenda"
                  value={datos.leyenda}
                  onChange={handleChange}
                  fullWidth
                  inputProps={{ maxLength: 255 }}
                  placeholder="Ej: Descuento por recibo de sueldo - Agosto"
                />
                <SoftTypography variant="caption" color="text">
                  Es el título que verá el cliente en la app. Si queda vacío se mostrará &quot;Transferencia de deuda a UPrO&quot;.
                </SoftTypography>
              </Grid>
            </Grid>

            <SoftBox
              mt={2}
              p={2}
              sx={{
                border: "1px solid #DBE4EE",
                borderRadius: 2,
                backgroundColor: "#F8FAFC",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <SoftTypography variant="caption" color="text">
                    Clientes seleccionados
                  </SoftTypography>
                  <SoftTypography variant="h6" fontWeight="bold">
                    {pagosCliente.length}
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <SoftTypography variant="caption" color="text">
                    Fecha de corte
                  </SoftTypography>
                  <SoftTypography variant="button" fontWeight="bold">
                    {fechaFin ? formatDateARG(fechaFin) : "-"}
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <SoftTypography variant="caption" color="text">
                    Total a pagar
                  </SoftTypography>
                  <SoftTypography variant="h6" fontWeight="bold" color="dark">
                    $ {formatMoneyPunto(totalSeleccionado || 0)}
                  </SoftTypography>
                </Grid>
              </Grid>
            </SoftBox>

            <SoftBox mt={2}>
              <SoftInput
                placeholder="Buscar por nombre o DNI"
                value={searchCliente}
                onChange={(e) => setSearchCliente(e.target.value)}
              />
            </SoftBox>

            <SoftBox mt={2}>
              <SoftTypography variant="caption" fontWeight="bold">
                Montos por cliente
              </SoftTypography>
              <SoftBox
                mt={1}
                p={1}
                sx={{
                  border: "1px solid #DBE4EE",
                  borderRadius: 2,
                  maxHeight: "340px",
                  overflowY: "auto",
                  backgroundColor: "#FFFFFF",
                }}
              >
                {pagosClienteFiltrados.length === 0 && (
                  <SoftTypography variant="caption" color="text" p={1} display="block">
                    No hay clientes coincidentes para mostrar.
                  </SoftTypography>
                )}
                {pagosClienteFiltrados.map((item) => (
                  <SoftBox
                    key={item.idCliente}
                    sx={{
                      border: "1px solid #E8EEF5",
                      borderRadius: 2,
                      p: { xs: 1.5, md: 2 },
                      mb: 1,
                      background: "linear-gradient(180deg, #FCFDFF 0%, #F8FBFF 100%)",
                    }}
                  >
                    <Grid container spacing={2} alignItems="stretch">
                      <Grid item xs={12} lg={8}>
                        <SoftBox
                          sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <SoftBox mb={1.5}>
                            <SoftTypography variant="button" fontWeight="bold" sx={{ display: "block", mb: 0.35 }}>
                              {item.nombre}
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text" display="block">
                              DNI: {item.dni}
                            </SoftTypography>
                          </SoftBox>
                          <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={4}>
                              <SoftBox
                                sx={{
                                  height: "100%",
                                  p: 1.25,
                                  borderRadius: 1.5,
                                  backgroundColor: "#FFFFFF",
                                  border: "1px solid #E6EDF5",
                                }}
                              >
                                <SoftTypography variant="caption" color="text" display="block" mb={0.5}>
                                  Saldo actual
                                </SoftTypography>
                                <SoftTypography variant="button" fontWeight="bold">
                                  $ {formatMoneyPunto(item.saldoActual || 0)}
                                </SoftTypography>
                              </SoftBox>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <SoftBox
                                sx={{
                                  height: "100%",
                                  p: 1.25,
                                  borderRadius: 1.5,
                                  backgroundColor: "#FFFFFF",
                                  border: "1px solid #E6EDF5",
                                }}
                              >
                                <SoftTypography variant="caption" color="text" display="block" mb={0.5}>
                                  Saldo a fecha de corte
                                </SoftTypography>
                                <SoftTypography variant="button" fontWeight="bold">
                                  $ {formatMoneyPunto(item.saldoFechaCorte || 0)}
                                </SoftTypography>
                              </SoftBox>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <SoftBox
                                sx={{
                                  height: "100%",
                                  p: 1.25,
                                  borderRadius: 1.5,
                                  backgroundColor: "#EEF6FF",
                                  border: "1px solid #CFE1F8",
                                }}
                              >
                                <SoftTypography variant="caption" color="text" display="block" mb={0.5}>
                                  Maximo pagable a fecha de corte
                                </SoftTypography>
                                <SoftTypography variant="button" fontWeight="bold">
                                  $ {formatMoneyPunto(item.montoMaximoPagable || 0)}
                                </SoftTypography>
                              </SoftBox>
                            </Grid>
                          </Grid>
                        </SoftBox>
                      </Grid>
                      <Grid item xs={12} lg={4}>
                        <SoftBox
                          sx={{
                            height: "100%",
                            p: 1.5,
                            borderRadius: 1.5,
                            backgroundColor: "#FFFFFF",
                            border: "1px solid #D8E3F0",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            gap: 1.5,
                          }}
                        >
                          <SoftBox>
                            <InputLabel htmlFor={`monto-${item.idCliente}`}>
                              <SoftTypography variant="caption" fontWeight="bold">
                                Monto a pagar
                              </SoftTypography>
                            </InputLabel>
                            <SoftInputNumber
                              id={`monto-${item.idCliente}`}
                              name={`monto-${item.idCliente}`}
                              value={item.monto}
                              onChange={(e) => handleChangeMonto(item.idCliente, e.target.value)}
                              fullWidth
                            />
                            <SoftTypography variant="caption" color="text" display="block" mt={0.75}>
                              Tope: $ {formatMoneyPunto(item.montoMaximoPagable || 0)}
                            </SoftTypography>
                          </SoftBox>
                          <SoftBox
                            sx={{
                              display: "flex",
                              gap: 1,
                              justifyContent: "flex-end",
                              flexWrap: "wrap",
                            }}
                          >
                            <SoftButton
                              variant="outlined"
                              color="dark"
                              size="small"
                              sx={{ width: { xs: "100%", sm: "120px" } }}
                              onClick={() => completarMontoCliente(item.idCliente)}
                            >
                              Completo
                            </SoftButton>
                            <SoftButton
                              variant="outlined"
                              color="secondary"
                              size="small"
                              sx={{ width: { xs: "100%", sm: "120px" } }}
                              onClick={() => limpiarMontoCliente(item.idCliente)}
                            >
                              Limpiar
                            </SoftButton>
                          </SoftBox>
                        </SoftBox>
                      </Grid>
                    </Grid>
                  </SoftBox>
                ))}
              </SoftBox>
              {hayMontosInvalidos && (
                <SoftTypography variant="caption" color="error" mt={1} display="block">
                  Revisa los montos: no pueden ser negativos ni superar el maximo pagable.
                </SoftTypography>
              )}
            </SoftBox>
          </SoftBox>

          <SoftBox
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            p={3}
            sx={{ borderTop: "1px solid #E5EAF2", backgroundColor: "#F8FAFC" }}
          >
            <SoftButton variant="gradient" color="primary" onClick={handleClose} disabled={loadingPagar}>
              <Icon fontSize="small" sx={{ verticalAlign: "middle", mr: 1 }}>
                close
              </Icon>
              Cancelar
            </SoftButton>
            <SoftButton
              variant="gradient"
              color="dark"
              onClick={handleOpenConfirm}
              disabled={
                hayMontosInvalidos ||
                pagosSeleccionados.length === 0 ||
                loadingPagar
              }
              sx={{ marginLeft: 2 }}
            >
              {loadingPagar ? (
                <CircularProgress sx={{ mr: 1 }} size={24} />
              ) : (
                <Icon fontSize="small" sx={{ verticalAlign: "middle", mr: 1 }}>
                  payment
                </Icon>
              )}
              Confirmar traslado
            </SoftButton>
          </SoftBox>

          <ModalConfirm
            open={openConfirm}
            handleClose={handleCloseConfirm}
            message={`Esta seguro que desea confirmar el traslado de deuda al ${fechaFin ? formatDateARG(fechaFin) : ""} por un total de $ ${formatMoneyPunto(totalSeleccionado || 0)}? Esta accion no se puede deshacer.`}
            func={() => {
              handlePagar();
            }}
          />
        </Card>
      </Fade>
    </Modal>
  );
}

ModalCargarPago.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  fechaFin: PropTypes.string,
  idSede: PropTypes.number,
  getReporte: PropTypes.func,
  onPagoRegistrado: PropTypes.func,
  clientes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      nombre: PropTypes.string,
      apellido: PropTypes.string,
      dni: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      saldoActual: PropTypes.number,
      saldoFechaCorte: PropTypes.number,
      montoMaximoPagable: PropTypes.number,
    })
  ),
};
